import React, { useMemo, useState } from 'react';
import HeaderRow from '@components/common/HeaderRow';
import PageTitle from '@components/common/PageTitle';
import { useActiveTenant } from '@states/active-tenant.atom';
import { useVisaApplicationReport } from '../hooks/use-visa-application-report.hook';
import {
  VisaApplicationReportItem,
  DashboardDateRangeEnum,
} from '../types/visa-application-report.types';
import VisaApplicationReportTable from '../components/visa-application-report-table.component';
import { useWorkflowList } from '@components/dashboard/admin/workflow/hooks/use-workflow.hook';
import { DEFAULT_PAGINATED_TABLE_DATA } from '@const/default-paginated-table-data.const';
import { WithPermissionCheck } from '@components/auth/WithPermissionCheck';
import type { Workflow } from '@components/dashboard/admin/workflow/types/workflow.types';
import type { PaginatedData } from '../../../../../../types/pagination.types';
import { useTranslation } from 'react-i18next';

const VisaApplicationReportContainer: React.FC = () => {
  const { t } = useTranslation();
  const { activeTenant } = useActiveTenant();

  const [dateRange, setDateRange] = useState<DashboardDateRangeEnum>(DashboardDateRangeEnum.DAY);
  const [selectedWorkflowIds, setSelectedWorkflowIds] = useState<string[]>([]);

  const { data: workflowListData, isLoading: isWorkflowsLoading } = useWorkflowList({
    page: 1,
    limit: 100,
  });

  const workflowOptions = useMemo(() => {
    const paginated: PaginatedData<Workflow> =
      (workflowListData as PaginatedData<Workflow> | undefined) ??
      (DEFAULT_PAGINATED_TABLE_DATA as PaginatedData<Workflow>);

    return paginated.docs.map((workflow: Workflow) => ({
      label: workflow.name,
      value: workflow.id,
    }));
  }, [workflowListData]);

  const {
    data: reportData,
    isLoading: isReportLoading,
    error,
  } = useVisaApplicationReport(
    {
      dateRange,
      dataSource: 'application',
      workflowIds: selectedWorkflowIds,
    },
    {
      enabled: !!activeTenant?.id && selectedWorkflowIds.length > 0,
    }
  );

  const isLoading = isReportLoading || isWorkflowsLoading;
  const responseData: VisaApplicationReportItem[] = reportData?.data ?? [];

  return (
    <>
      <PageTitle title={t('applicationReport.title')} />
      <HeaderRow
        title={t('applicationReport.title')}
        showBackButton={false}
        showCreateButton={false}
        showBreadcrumbs={true}
      />

      <div className="row mt-3">
        <div className="col-12">
          <VisaApplicationReportTable
            data={responseData}
            loading={isLoading}
            error={error}
            dateRange={dateRange}
            onDateRangeChange={(value: DashboardDateRangeEnum) => setDateRange(value)}
            workflowOptions={workflowOptions}
            selectedWorkflowIds={selectedWorkflowIds}
            onWorkflowChange={setSelectedWorkflowIds}
          />
        </div>
      </div>
    </>
  );
};

export default WithPermissionCheck(VisaApplicationReportContainer, 'workflow.view');
